<template>
  <div class="page-container">
    <div class="d-flex align-items-center w-100">
      <h5 class="mb-0">Медиафайлы</h5>
      <b-button v-if="canEditMedia" class="custom-btn-primary ml-auto" @click="onAdd" :disabled="isBusy || isDisabled">
        <v-icon name="buttons/plus" :size="12"/>
        Добавить
      </b-button>
    </div>

    <div class="page-body">
      <b-row style="gap: 24px;">
        <b-col cols="12" xl="4" v-for="(item, index) in items" :key="item.id">
          <b-card no-body class="adaptive-card custom-card h-100" @click="onEdit(index)">
            <b-card-body class="d-flex flex-column">
              <b-row class="mb-2">
                <b-col>
                  <h5 class="mb-0">{{ item.title }}</h5>
                </b-col>
              </b-row>
              <b-row class="mb-1 flex-grow-1 text-small text-muted text-clamp">
                <b-col>
                  {{ item.description || 'Нет описания' }}
                </b-col>
              </b-row>
              <b-row class="px-3">
                <div v-if="item.isLoadingFiles" class="media-preview-row d-flex align-items-center justify-content-center bg-light rounded text-muted small">
                  <b-spinner small class="mr-2" variant="success"/>
                  <small>Загрузка файлов...</small>
                </div>

                <div class="media-preview-row d-flex" v-else-if="item.files.length > 0">
                  <template v-for="(file, index) in item.files">
                    <div
                      v-if="index < 3"
                      :key="index"
                      class="media-preview-col mr-1 position-relative border"
                    >
                      <div v-if="!file.hasError">
                        <video v-if="file.type.startsWith('video')" :src="file.url" class="media-obj" preload="metadata" @error="$set(file, 'hasError', true)"/>
                        <img v-else :src="file.url" class="media-obj" alt="img" @error="$set(file, 'hasError', true)"/>
                      </div>

                      <div v-else class="h-100 w-100 d-flex flex-column align-items-center justify-content-center text-muted">
                        <font-awesome-icon icon="exclamation-circle" class="mb-1 text-danger"/>
                        <small>Ошибка загрузки</small>
                      </div>

                      <div v-if="index === 2 && item.files.length > 3" class="more-overlay">
                        +{{ item.files.length - 3 }}
                      </div>
                    </div>
                  </template>
                </div>

                <div v-else class="media-preview-row d-flex align-items-center justify-content-center bg-light rounded text-muted small">
                  <small>Нет файлов</small>
                </div>
              </b-row>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <div v-if="items.length === 0" class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>Нет добавленных медиафайлов.</h5>
        <p>Добавьте новые файлы прямо сейчас</p>
        <b-btn v-if="canEditMedia" class="custom-btn-primary mt-1" :disabled="isBusy || isDisabled" @click="onAdd">
          <v-icon name="buttons/plus" :size="12" class="mr-1"/>
          Новый файл
        </b-btn>
      </div>
    </div>

    <b-modal
      id="modal-media"
      ref="modal_media"
      size="xl"
      :title='mediaItem.id === 0 ? "Новая запись" : `Редактирование медиафайлов "${mediaItem.title}".`'
      centered
      scrollable
      :ok-only="true"
      no-close-on-backdrop
      no-close-on-esc
      v-model="showModalMediaItems"
      @hidden="cleanUpModal"
    >
      <b-row>
        <b-col>
          <b-form-group label="Заголовок">
            <b-form-input
              v-model="mediaItem.title"
              :disabled="isDisabled || !canEditMedia"
              size="sm"
              type="text"
              placeholder="Введите заголовок"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group label="Описание">
            <b-form-textarea
              v-model="mediaItem.description"
              :disabled="isDisabled || !canEditMedia"
              placeholder="Нет описания"
              size="sm"
              rows="3"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group label="Файлы">
            <b-form-file
              v-if="canEditMedia"
              ref="mediaFileInput"
              multiple
              :disabled="isDisabled"
              size="sm"
              placeholder="Файлы не выбраны"
              browse-text="Выбрать"
              accept="image/*,video/*,.pdf,.xlsx,.xls,.doc,.docx,.txt"
              @change="handleUpload"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="4" class="mt-4 position-relative" v-for="(file, index) in mediaItem.files" :key="index">
          <div class="modal-media-size border rounded overflow-hidden position-relative">
            <div class="modal-media-position" @click="!file.hasError ? openMediaView(file) : null">
              <div v-if="!file.hasError" class="h-100 w-100 d-flex align-items-center justify-content-center cursor-pointer">
                <video v-if="file.category === 'video'" :src="file.url" class="media-obj" preload="metadata" @error="$set(file, 'hasError', true)"/>

                <img v-else-if="file.category === 'image'" :src="file.url" class="media-obj" alt="img" @error="$set(file, 'hasError', true)"/>

                <div v-else class="text-muted d-flex flex-column align-items-center w-100 px-3 text-center">
                  <font-awesome-icon :icon="getFileIcon(file.fileName)" size="3x" class="mb-2"/>
                  <small class="text-truncate w-100">{{ file.fileName }}</small>
                </div>
              </div>

              <div v-else class="h-100 w-100 d-flex align-items-center justify-content-center text-muted pl-3">
                <font-awesome-icon icon="exclamation-circle" class="mr-2 text-danger" size="lg"/>
                Ошибка загрузки
              </div>
            </div>
          </div>
          <b-button
            v-if="canEditMedia"
            variant="danger"
            size="sm"
            :disabled="isDisabled"
            class="p-1 remove-btn-position rounded-circle d-flex align-items-center justify-content-center"
            @click="onRemoveFile(index)"
          >
            <font-awesome-icon icon="times-circle"/>
          </b-button>
          <b-button
            variant="info"
            size="sm"
            class="p-1 download-btn-position rounded-circle d-flex align-items-center justify-content-center"
            :href="file.url"
            :download="file.fileName"
          >
            <font-awesome-icon icon="download" style="font-size: 12px;"/>
          </b-button>
        </b-col>
      </b-row>

      <template #modal-footer>
        <b-button v-if="canEditMedia && isEditing" :disabled="isDisabled" class="custom-btn-danger" @click="onRemoveMedia()">
          Удалить
        </b-button>
        <b-button class="custom-btn-secondary" @click="hideModal()">
          Отмена
        </b-button>
        <b-button v-if="canEditMedia" class="custom-btn-primary" :disabled="isDisabled" @click.prevent="handleSubmit()">
          Сохранить
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-passport-file-view"
      v-model="showMediaViewModal"
      size="xl"
      centered
      hide-footer
      hide-header
      body-class="p-0 bg-dark"
    >
      <div class="d-flex justify-content-center align-items-center position-relative" style="min-height: 50vh;">
        <b-button
          variant="link"
          class="view-btn-position text-white position-absolute"
          @click="hideMediaView()"
        >
          <font-awesome-icon icon="times"/>
        </b-button>
        <video
          v-if="viewFile && viewFile.type.startsWith('video')"
          controls
          autoplay
          :src="viewFile.url"
          class="w-100 h-auto view-media-height"
        />
        <img
          v-else-if="viewFile"
          :src="viewFile.url"
          class="img-fluid view-media-height"
          alt="view"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import helpers from "../../utilites/helpers";

export default {
  name: 'Media',
  mixins: [ helpers ],
  props: {
    entityId: { type: Number, required: true },
    entityKey: { type: String, required: true },
    apiConfig: { type: Object, required: true },
    canEditMedia: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false }
  },
  data() {
    return {
      items: [],
      mediaItem: {
        id: 0,
        title: '',
        description: '',
        files: []
      },
      filesToDelete: [],
      isBusy: false,
      showModalMediaItems: false,
      showMediaViewModal: false,
      viewFile: null,
      isEditing: false,
    };
  },
  beforeDestroy() {
    this.revokeAllUrls();
  },
  watch: {
    entityId: {
      handler(newValue) {
        if (newValue) {
          this.getItems();
        }
      }, immediate: true
    }
  },
  methods: {
    async getItems() {
      this.revokeAllUrls();
      this.isBusy = true;

      const result = await this.apiConfig.getMedia(this.entityId);

      if (result.error) {
        this.showToast('Ошибка загрузки медиафайлов.', 'Ошибка', 'danger');
        this.isBusy = false;
        return;
      }

      this.items = (result.data || []).map(item => ({ ...item, files: [], isLoadingFiles: true }));
      this.isBusy = false;

      this.items.forEach((item, index) => this.getMediaFiles(index, item.id));
    },
    async getMediaFiles(index, mediaId) {
      const payload = {
        id: mediaId,
        [this.entityKey]: this.entityId
      };

      const result = await this.apiConfig.getFiles(payload);

      if (result.error) {
        this.$set(this.items[index], 'isLoadingFiles', false);
        return;
      }

      const filesData = result.data || [];

      const blobItems = await Promise.all(filesData.map(async f => {
        const contentResult = await this.apiConfig.getFileDownload(this.entityId, mediaId, f.id);
        let objectUrl = '';

        const mimeType = this.guessMimeType(f.file_name);
        const category = this.getFileCategory(mimeType, f.file_name);

        if (contentResult.data) {
          const blob = new Blob([contentResult.data], { type: mimeType });
          objectUrl = URL.createObjectURL(blob);
        }

        return {
          id: f.id,
          type: mimeType,
          category: category,
          url: objectUrl,
          isUploaded: true,
          fileName: f.file_name,
          hasError: !!contentResult.error
        };
      }));

      this.$set(this.items, index, { ...this.items[index], files: blobItems, isLoadingFiles: false });
    },
    onAdd() {
      if (!this.entityId) {
        this.showToast('Сначала нажмите кнопку "Сохранить".', 'Ошибка', 'danger');
        return;
      }

      this.isEditing = false;
      this.filesToDelete = [];

      this.mediaItem = {
        id: 0,
        title: '',
        description: '',
        files: []
      };

      if (this.$refs.mediaFileInput) this.$refs.mediaFileInput.reset();
      this.showModalMediaItems = true;
    },
    onEdit(index) {
      this.isEditing = true;
      this.filesToDelete = [];

      const item = this.items[index];
      this.mediaItem = {
        id: item.id,
        title: item.title,
        description: item.description,
        files: [...item.files]
      };

      if (this.$refs.mediaFileInput) this.$refs.mediaFileInput.reset();

      this.showModalMediaItems = true;
    },
    handleUpload(v) {
      Array.from(v.target.files).forEach(file => {
        const url = URL.createObjectURL(file);
        const mimeType = file.type || this.guessMimeType(file.name);
        const category = this.getFileCategory(mimeType, file.name);

        this.mediaItem.files.push({
          fileObj: file,
          url: url,
          type: mimeType,
          category: category,
          isUploaded: false,
          fileName: file.name,
          hasError: false
        });
      });
    },
    async handleSubmit() {
      if (!this.mediaItem.title) {
        this.showToast('Введите заголовок.', 'Ошибка', 'danger');
        return;
      }

      this.isBusy = true;

      const mediaData = {
        id: this.mediaItem.id,
        [this.entityKey]: this.entityId,
        title: this.mediaItem.title,
        description: this.mediaItem.description,
        active: true
      };

      const method = this.mediaItem.id !== 0 ? this.apiConfig.putMedia : this.apiConfig.postMedia;
      const result = await method(mediaData);

      if (result.error) {
        this.showToast('Ошибка при сохранении.', 'Ошибка', 'danger');
        this.isBusy = false;
        return;
      }

      const mediaId = result.data && result.data.id ? result.data.id : this.mediaItem.id;
      const dataToUpload = [];

      if (this.filesToDelete.length > 0) {
        this.filesToDelete.forEach(fileId => {
          dataToUpload.push(this.apiConfig.deleteFile(this.entityId, mediaId, fileId));
        });
      }

      const filesToUpload = this.mediaItem.files.filter(f => f.fileObj);

      if (filesToUpload.length > 0) {
        filesToUpload.forEach(fileItem => {
          const formData = new FormData();
          formData.append('file', fileItem.fileObj);
          dataToUpload.push(this.apiConfig.postFile(this.entityId, mediaId, formData));
        });
      }

      await Promise.all(dataToUpload);
      this.isBusy = false;
      this.showToast('Данные сохранены.', 'Успех', 'success');
      this.hideModal();
      await this.getItems();
    },
    onRemoveFile(index) {
      const file = this.mediaItem.files[index];

      if (file.isUploaded && file.id) {
        this.filesToDelete.push(file.id);
      } else {
        this.revokeFileUrl(file.url);
      }

      this.mediaItem.files.splice(index, 1);
    },
    async onRemoveMedia() {
      this.showModalDelete('Удаление', `Удалить файлы?`, async () => {
        const payload = {
          id: this.mediaItem.id,
          [this.entityKey]: this.entityId
        };
        await this.apiConfig.deleteMedia(payload);
        this.hideModal();
        await this.getItems();
      });
    },
    openMediaView(file) {
      this.viewFile = file;
      this.showMediaViewModal = true;
    },
    hideMediaView() {
      this.showMediaViewModal = false;
    },
    hideModal() {
      this.showModalMediaItems = false;
    },
    cleanUpModal() {
      this.mediaItem.files.forEach(f => { if (!f.isUploaded) this.revokeFileUrl(f.url); });
      this.mediaItem.files = [];
      this.filesToDelete = [];
    },
    revokeAllUrls() {
      this.items.forEach(item => { if (item.files) item.files.forEach(f => this.revokeFileUrl(f.url)) });
      if (this.mediaItem.files) this.mediaItem.files.forEach(f => this.revokeFileUrl(f.url));
    },
    revokeFileUrl(url) {
      if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
    },
    getFileCategory(mimeType, fileName = '') {
      if (mimeType.startsWith('video/') || fileName.match(/\.(mp4|webm|ogg|mov)$/i)) return 'video';
      if (mimeType.startsWith('image/') || fileName.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) return 'image';
      if (mimeType === 'application/pdf' || fileName.match(/\.pdf$/i)) return 'pdf';
      return 'document';
    },
    getFileIcon(fileName = '') {
      if (fileName.match(/\.(xls|xlsx|csv)$/i)) return 'file-excel';
      if (fileName.match(/\.(doc|docx)$/i)) return 'file-word';
      if (fileName.match(/\.pdf$/i)) return 'file-pdf';
      if (fileName.match(/\.(zip|rar|7z)$/i)) return 'file-archive';
      return 'file-alt';
    },
    guessMimeType(fileName = '') {
      const ext = fileName.split('.').pop().toLowerCase();
      const mimeTypes = {
        'pdf': 'application/pdf',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'xls': 'application/vnd.ms-excel',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'png': 'image/png', 'jpg': 'image/jpeg', 'jpeg': 'image/jpeg', 'mp4': 'video/mp4'
      };
      return mimeTypes[ext] || 'application/octet-stream';
    }
  }
};
</script>

<style scoped>
.adaptive-card {
  min-width: 400px;
  flex: 1 0 400px;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.2s, box-shadow 0.2s;
  border: 1px solid rgba(0,0,0,0.1);
}

.adaptive-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  z-index: 2;
}

.text-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 3.7rem;
}

.more-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.4);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.media-preview-row {
  height: 100px;
  width: 100%;
}

.media-preview-col {
  flex: 0 0 122px;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  background-color: #f8f9fa;
}

.media-obj {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #f8f9fa;
}

.modal-media-size {
  padding-top: 70%;
}

.modal-media-position {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  cursor: pointer;
}

.remove-btn-position {
  position: absolute;
  top: -5px;
  right: 5px;
}

.download-btn-position {
  position: absolute;
  top: 23px;
  right: 5px;
}

.view-btn-position {
  top: 10px;
  right: 10px;
  z-index: 100;
  font-size: 1.5rem;
}

.view-media-height {
  max-height: 100vh;
}
</style>



// WEBPACK FOOTER //
// src/utilites/commonUi/Media.vue